import { Autocomplete, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import moment from 'moment'
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service'
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model'
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service'
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import GroupService from '../../../../shared/services/group/group.service'
import { IGroup } from '../../../../shared/services/group/group.model'
import { createDisplayFormatSpendBasedConversion, sortByConversionCode } from '../../../../shared/utils/conversion-utils'
import { formatGroupLabel, sortGroupsByGhgCategory } from '../../../../shared/constants/ghg-category'
import { NominalMappingType } from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'

// A displayable spend based conversion (adds the displayText field produced by the shared formatter).
type DisplaySpendConversion = ISpendBasedConversion & { displayText: string }

export interface INominalOutcomeValue {
  mappingType: NominalMappingType
  spendBasedConversionId?: string | null
  conversionSourceId?: string | null
  excludeReason?: string | null
  groupId?: string | null
}

export const NominalMappingOutcomePicker: FunctionComponent<{
  value: INominalOutcomeValue,
  onChange: (next: INominalOutcomeValue) => void,
  compact?: boolean,
  // When rendered many times (the bulk table), the parent loads these lists once and passes them in
  // so we do not fire one identical request per row.
  sources?: IConversionSource[],
  groups?: IGroup[],
}> = ({ value, onChange, compact = false, sources, groups }) => {
  //Services
  const conversionSourceService = new ConversionSourceService()
  const spendBasedConversionService = new SpendBasedConversionService()
  const groupService = new GroupService()
  //State
  const [loadedSources, setLoadedSources] = useState<IConversionSource[]>([])
  const [loadedGroups, setLoadedGroups] = useState<IGroup[]>([])
  const [spendBasedConversions, setSpendBasedConversions] = useState<DisplaySpendConversion[]>([])

  const conversionSources = sources ?? loadedSources
  const availableGroups = groups ?? loadedGroups
  const controlSize = compact ? 'small' : 'medium'

  //Load conversion sources once on mount, unless the parent supplied them.
  useEffect(() => {
    if (sources) return
    conversionSourceService.getAll(true, 'spendBasedConversion')
      .then(response => setLoadedSources(response))
      .catch(err => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Load groups once on mount, unless the parent supplied them.
  useEffect(() => {
    if (groups) return
    groupService.getGroups()
      .then(response => setLoadedGroups(response))
      .catch(err => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Helper to load the activities for a given conversion source.
  const loadActivitiesForSource = (sourceId: string) => {
    spendBasedConversionService
      .getSpendBasedConversionsForConversionSource(sourceId, moment().year().toString(), true, true)
      .then((response: any) => {
        const rows = Array.isArray(response) ? response : response?.rows
        const formatted = (rows || []).map((conversion: ISpendBasedConversion) => createDisplayFormatSpendBasedConversion(conversion) as DisplaySpendConversion)
        setSpendBasedConversions(formatted)
      })
      .catch(err => console.log(err))
  }

  //When mounting (or the incoming source changes) with an existing spend value, load that source's activities so the current activity displays.
  useEffect(() => {
    if (value.mappingType === 'spend' && value.conversionSourceId) {
      loadActivitiesForSource(value.conversionSourceId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.conversionSourceId])

  //Event Handlers
  const outcomeChangeHandler = (e: SelectChangeEvent) => {
    const nextType = e.target.value as NominalMappingType
    if (nextType === 'exclude') {
      onChange({ mappingType: 'exclude', spendBasedConversionId: null, conversionSourceId: null, excludeReason: value.excludeReason ?? '', groupId: value.groupId ?? null })
    } else if (nextType === 'private') {
      // Nothing about the line is kept, so there is no factor, reason or group to carry.
      onChange({ mappingType: 'private', spendBasedConversionId: null, conversionSourceId: null, excludeReason: null, groupId: null })
    } else {
      onChange({ mappingType: 'spend', spendBasedConversionId: null, conversionSourceId: null, excludeReason: null, groupId: value.groupId ?? null })
    }
  }

  const sourceChangeHandler = (e: SelectChangeEvent) => {
    const sourceId = e.target.value
    setSpendBasedConversions([])
    onChange({ mappingType: 'spend', conversionSourceId: sourceId, spendBasedConversionId: null, excludeReason: null, groupId: value.groupId ?? null })
    if (sourceId) {
      loadActivitiesForSource(sourceId)
    }
  }

  const activityChangeHandler = (_e: any, activity: DisplaySpendConversion | null) => {
    onChange({
      mappingType: 'spend',
      conversionSourceId: value.conversionSourceId ?? null,
      spendBasedConversionId: activity ? activity.id : null,
      excludeReason: null,
      groupId: value.groupId ?? null,
    })
  }

  const reasonChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, mappingType: 'exclude', excludeReason: e.target.value, spendBasedConversionId: null, conversionSourceId: null, groupId: value.groupId ?? null })
  }

  const groupChangeHandler = (e: SelectChangeEvent) => {
    const nextGroupId = e.target.value
    onChange({ ...value, groupId: nextGroupId || null })
  }

  const selectedActivity = spendBasedConversions.find(conversion => conversion.id === value.spendBasedConversionId) || null

  return (
    <Stack
      useFlexGap
      spacing={compact ? 1 : 2}
      direction={compact ? { xs: 'column', md: 'row' } : 'column'}
      alignItems={compact ? { xs: 'stretch', md: 'flex-start' } : 'stretch'}
      sx={{ flexWrap: compact ? 'wrap' : 'nowrap' }}
    >
      <FormControl size={controlSize} sx={{ minWidth: compact ? 160 : undefined }} fullWidth={!compact}>
        <InputLabel>Outcome</InputLabel>
        <Select
          label='Outcome'
          value={value.mappingType}
          onChange={outcomeChangeHandler}
          sx={{ color: 'text.primary' }}>
          <MenuItem value='spend'>Spend factor</MenuItem>
          <MenuItem value='exclude'>Exclude</MenuItem>
          <MenuItem value='private'>Don't import</MenuItem>
          {/* A code already stored under the retired "captured via activity data" outcome still has
              to render as the current value, or the select shows blank and reads as lost data. It is
              not offerable: picking anything else moves the code onto a live outcome for good. */}
          {value.mappingType === 'activity' && (
            <MenuItem value='activity' disabled>Captured via activity data (retired)</MenuItem>
          )}
        </Select>
      </FormControl>

      {value.mappingType === 'spend' && (
        <>
          <FormControl size={controlSize} sx={{ minWidth: compact ? 200 : undefined }} fullWidth={!compact}>
            <InputLabel>Conversion factor source</InputLabel>
            <Select
              label='Conversion factor source'
              value={value.conversionSourceId || ''}
              onChange={sourceChangeHandler}
              sx={{ color: 'text.primary' }}>
              {conversionSources.slice().sort((a, b) => a.source.localeCompare(b.source)).map(source => (
                <MenuItem key={source.id} value={source.id}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <span>{source.source}</span>
                    {source.country && (
                      <img loading='lazy' width='20' src={source.country.imageUrl} alt={`${source.country.name} flag`} />
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {value.conversionSourceId && (
            <Autocomplete
              size={controlSize}
              sx={{ minWidth: compact ? 260 : undefined, width: compact ? undefined : '100%' }}
              options={sortByConversionCode(spendBasedConversions)}
              getOptionLabel={(option) => option.displayText || ''}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              value={selectedActivity}
              onChange={activityChangeHandler}
              renderInput={(params) => <TextField {...params} label='Spend activity' />}
            />
          )}

          <FormControl size={controlSize} sx={{ minWidth: compact ? 220 : undefined }} fullWidth={!compact}>
            <InputLabel>Group (optional)</InputLabel>
            <Select
              label='Group (optional)'
              value={value.groupId || ''}
              onChange={groupChangeHandler}
              sx={{ color: 'text.primary' }}>
              <MenuItem value=''>None</MenuItem>
              {sortGroupsByGhgCategory(availableGroups).map(group => (
                <MenuItem key={group.id} value={group.id}>{formatGroupLabel(group)}</MenuItem>
              ))}
            </Select>
            {/* The explanatory caption is helpful in the single add/edit dialog, but repeated down the
                compact bulk table it just wraps and clutters every row, so show it only when not compact. */}
            {!compact && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
                Set this to place the code in the right scope, for example a capital-goods group for Scope 3 Category 2 (capex).
              </Typography>
            )}
          </FormControl>
        </>
      )}

      {value.mappingType === 'activity' && (
        <Typography variant='caption' color='text.secondary'>
          This outcome has been retired. It behaves exactly as Exclude: the spend on this code is dropped.
          Choose Spend factor or Exclude to move it across.
        </Typography>
      )}

      {value.mappingType === 'private' && (
        <Typography variant='caption' color='text.secondary'>
          Lines on this code are never brought in from your ledger, so nothing about them is stored here.
          Use it for wages, PAYE, pensions and other personal data. Lines already brought in are deleted
          when you save, unless they have been committed.
        </Typography>
      )}

      {value.mappingType === 'exclude' && (
        <FormControl size={controlSize} sx={{ minWidth: compact ? 220 : undefined }} fullWidth={!compact}>
          <TextField
            size={controlSize}
            label='Reason (optional)'
            placeholder='e.g. Depreciation, VAT / balance sheet'
            value={value.excludeReason || ''}
            onChange={reasonChangeHandler}
          />
        </FormControl>
      )}
    </Stack>
  )
}
